/**
 * Full UrbaniX report HTML (same document as the site print / PDF view).
 */
import { AI_TIPS_SECTION_TITLE, AI_TIP_ILA, AI_TIP_TABU } from '../data/aiOutputTips';
import {
  computeEligibilityGoNoGo,
  ELIGIBILITY_CARD_STATUS_LABELS,
  ELIGIBILITY_SECTION_INTRO,
} from './eligibilityGoNoGo';
import { isRegisteredAsIlaLease } from './ilaLeaseOwnership';

function escapeHtmlPdf(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildUrbanixReportHtml(recommendation, feasibilityData, searchResults) {
  if (!recommendation) return '';
  const palette = {
    primary: '#1d3557',
    secondary: '#2985ad',
    softBg: '#fafafb',
    bodyText: '#1f2a37',
    mutedText: '#667085',
    divider: '#d8def4',
    success: '#047857',
    warning: '#b45309',
    error: '#dc2626',
    neutral: '#667085',
    accentCard: '#ffffff',
  };

  const getDerivedEstimateRange = (estimatedApartments) => {
    if (!Number.isFinite(estimatedApartments)) return null;
    if (estimatedApartments >= 50) return 'very_high';
    if (estimatedApartments >= 20) return 'high';
    if (estimatedApartments >= 10) return 'medium';
    if (estimatedApartments >= 5) return 'low';
    if (estimatedApartments >= 1) return 'very_low';
    return null;
  };

  const getEstimateRangeLabel = (range) => {
    const ranges = {
      very_low: 'מבנה בעל מספר דירות נמוך מאוד',
      low: 'מבנה בעל מספר דירות נמוך',
      medium: 'מבנה בעל מספר דירות בינוני',
      high: 'מבנה בעל מספר דירות גבוה',
      very_high: 'מבנה בעל מספר דירות גבוה מאוד',
    };
    return ranges[range] || 'לא צוין';
  };

  const summaryBlocks = (() => {
    if (!recommendation.summary) return [];
    let summaryText = recommendation.summary;
    const sectionHeadings = ['חוזקות:', 'חולשות:', 'צעדים להמשך:'];
    for (const heading of sectionHeadings) {
      summaryText = summaryText.replace(new RegExp(`(?<!^)(?<!\n\n)\\s*${heading}`, 'g'), `\n\n${heading}`);
    }
    const paragraphs = summaryText
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);
    const fallbackLabels = ['סקירת נתונים', 'חוזקות', 'חולשות', 'צעדים להמשך'];
    const headingPattern = /^([^\n:]{2,30}):\s*/;
    return paragraphs.map((para, idx) => {
      const match = para.match(headingPattern);
      if (match) {
        return { label: match[1].trim(), text: para.slice(match[0].length).trim() };
      }
      return { label: idx < fallbackLabels.length ? fallbackLabels[idx] : null, text: para };
    });
  })();

const style = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Noto Sans Hebrew', Arial, sans-serif; direction: rtl;
         color: ${palette.bodyText}; padding: 40px; line-height: 1.7; font-size: 14px; }
  h1 { font-size: 24px; font-weight: 700; border-bottom: 3px solid ${palette.primary};
       padding-bottom: 10px; margin-bottom: 6px; color: ${palette.primary}; }
  .subtitle { font-size: 12px; color: ${palette.mutedText}; margin-bottom: 28px; }
  .section-divider { border: none; border-top: 2px solid ${palette.divider}; margin: 28px 0; }
  h2 { font-size: 18px; font-weight: 700; color: ${palette.primary};
       border-bottom: 1px solid #ddd; padding-bottom: 4px;
       margin: 24px 0 14px; }
  h3 { font-size: 15px; font-weight: 700; color: ${palette.secondary}; margin: 16px 0 8px; }
  .opening { font-style: italic; color: ${palette.bodyText}; margin-bottom: 24px;
              padding: 12px 16px; background: ${palette.softBg}; border-right: 3px solid ${palette.secondary}; border-radius: 4px; }
  .data-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 16px; }
  .data-row { display: flex; gap: 8px; padding: 4px 0; border-bottom: 1px solid #f0f0f0; }
  .data-label { font-weight: 600; color: ${palette.neutral}; min-width: 120px; flex-shrink: 0; }
  .data-val { color: ${palette.bodyText}; }
  .elig-section-intro { font-size: 12px; line-height: 1.55; color: #475569; margin: 0 0 14px; max-width: 720px; }
  .elig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0; }
  .elig-card { padding: 12px 14px; border-radius: 8px; border-right: 4px solid #ccc; }
  .elig-card.pass { border-right-color: ${palette.success}; background: #f0fdf4; }
  .elig-card.fail { border-right-color: ${palette.error}; background: #fef2f2; }
  .elig-card.unknown { border-right-color: ${palette.secondary}; background: #eff6ff; }
  .elig-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; }
  .elig-status { font-size: 12px; font-weight: 600; }
  .elig-card.pass .elig-status { color: ${palette.success}; }
  .elig-card.fail .elig-status { color: ${palette.error}; }
  .elig-card.unknown .elig-status { color: ${palette.secondary}; }
  .elig-desc { font-size: 12px; color: ${palette.neutral}; margin-top: 4px; line-height: 1.5; }
  .item { margin-bottom: 14px; }
  .item-label { font-size: 13px; font-weight: 700; color: ${palette.secondary}; margin-bottom: 2px; }
  .item p { font-size: 14px; line-height: 1.7; color: ${palette.bodyText}; }
  .metric-inline { font-size: 14px; margin-bottom: 14px; line-height: 1.6; }
  .metric-row { display: flex; gap: 24px; margin-bottom: 14px;
                 padding: 10px 16px; background: ${palette.softBg}; border-radius: 6px; }
  .metric { display: flex; align-items: baseline; gap: 6px; }
  .metric-val { font-size: 20px; font-weight: 700; color: ${palette.bodyText}; }
  .metric-lbl { font-size: 12px; color: ${palette.mutedText}; }
  .summary-label { font-size: 13px; font-weight: 700; color: ${palette.secondary}; margin-bottom: 4px; }
  .summary-text { font-size: 14px; line-height: 1.7; white-space: pre-line; margin-bottom: 16px; }
  .likelihood-row { display: flex; gap: 12px; align-items: center; margin: 14px 0;
                      padding: 10px 16px; background: ${palette.softBg}; border-radius: 6px; }
  .likelihood-label { font-weight: 600; color: ${palette.neutral}; }
  .likelihood-val { font-weight: 700; font-size: 16px; color: ${palette.primary}; }
  .ai-tips-wrap { margin-top: 20px; padding: 16px 18px; background: ${palette.accentCard};
                  border: 1px solid #e2e8f0; border-radius: 10px; }
  .ai-tips-wrap h3 { margin-top: 0; margin-bottom: 12px; }
  .tips-tip-block { margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid #e2e8f0; }
  .tips-tip-block:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
  .tips-tip-title { font-weight: 700; font-size: 14px; color: ${palette.primary}; margin-bottom: 8px; }
  .tips-tip-block p { font-size: 14px; line-height: 1.7; color: ${palette.bodyText}; margin: 0 0 8px; }
  .tips-tip-block p:last-child { margin-bottom: 0; }
  .tips-disclaimer { font-size: 12px !important; color: #64748b !important; line-height: 1.6 !important; }
  .plans-table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }
  .plans-table th { background: #f1f5f9; padding: 6px 10px; text-align: right; font-weight: 600; border-bottom: 2px solid #ddd; }
  .plans-table td { padding: 5px 10px; border-bottom: 1px solid #eee; }
  .plans-table tr:nth-child(even) { background: #fafafa; }
  .txn-table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }
  .txn-table th { background: #f1f5f9; padding: 6px 10px; text-align: right; font-weight: 600; border-bottom: 2px solid #ddd; }
  .txn-table td { padding: 5px 10px; border-bottom: 1px solid #eee; }
  .footer { margin-top: 36px; padding-top: 12px; border-top: 2px solid ${palette.primary};
             font-size: 11px; color: #999; text-align: center; }
  .page-break { break-before: page; page-break-before: always; }
  @media print { body { padding: 20px; } }
`;

// --- Helpers ---
const get = (key) => recommendation?.[key] || '';
const ba = recommendation?.buildingAnalysis || {};
const estimatedApts = Number.isFinite(ba.estimatedApartments) ? ba.estimatedApartments : '—';
const derivedR = getDerivedEstimateRange(ba.estimatedApartments);
const rangeLabel = getEstimateRangeLabel(derivedR || ba.estimateRange);
const fmtPrint = (t) => (t || '')
  .replace(/\s+(\d+)\)/g, (m, n) => Number(n) > 1 ? `<br>${n})` : m)
  .replace(/\n/g, '<br>');
const likelihoodLabels = { very_low: 'נמוך מאוד', low: 'נמוך', medium: 'בינוני', high: 'גבוה', very_high: 'גבוה מאוד' };

// --- Extract property data ---
const fInput = feasibilityData?.feasibilityInput || {};
const fResult = feasibilityData?.feasibilityResult || {};
const bInfo = fResult.buildingInfo || {};
const propertyInfo = searchResults || {};
const rawBuildingData = propertyInfo.buildingData || {};
const advancedLayers = propertyInfo.advancedLayers || {};
const socioEconomic = advancedLayers.socioEconomic || {};
const buildingStats = advancedLayers.buildingStats || {};
const marketData = fInput.marketData || {};
const address = propertyInfo.address || fInput.address || '';

// Cross-source floor/height sanity check for print view
const buildingData = (() => {
  if (rawBuildingData.floorsOverridden) return rawBuildingData;
  const rawFloors = rawBuildingData.floors ? parseInt(rawBuildingData.floors) : NaN;
  const bestHeight = parseFloat(rawBuildingData.height) || parseFloat(buildingStats['גובה המבנה במטרים']) || NaN;
  if (!isNaN(rawFloors) && rawFloors > 0 && !isNaN(bestHeight) && bestHeight > 0 && bestHeight / rawFloors > 6) {
    const corrected = Math.floor(bestHeight / 3.6);
    if (corrected >= 1 && corrected !== rawFloors) {
      return { ...rawBuildingData, floors: rawFloors, floorsOverridden: true, correctedFloors: corrected,
        floorsOverrideReason: `נתון זה אינו תואם לגובה המבנה (${bestHeight.toFixed ? bestHeight.toFixed(1) : bestHeight} מ׳). המערכת משתמשת ב-${corrected} קומות (הערכה) לצורך החישובים.` };
    }
  }
  return rawBuildingData;
})();

// --- Property identification ---
const dataRows = [];
if (address) dataRows.push(['כתובת', address]);
if (propertyInfo.municipality || fInput.municipalityName) dataRows.push(['רשות מקומית', propertyInfo.municipality || fInput.municipalityName]);
if (fInput.gush || propertyInfo.gush) dataRows.push(['גוש', fInput.gush || propertyInfo.gush]);
if (fInput.helka || propertyInfo.helka) dataRows.push(['חלקה', fInput.helka || propertyInfo.helka]);
if (propertyInfo.subHelka) dataRows.push(['תת-חלקה', propertyInfo.subHelka]);
if (fInput.lotAreaSqm) dataRows.push(['שטח חלקה', `${Math.round(fInput.lotAreaSqm)} מ"ר`]);

// Building data
const year = fInput.constructionYear || bInfo.constructionYear || buildingData.year;
if (year && String(year) !== '1900') dataRows.push(['שנת בנייה', String(year)]);
else if (year && String(year) === '1900') dataRows.push(['שנת בנייה', 'לא ידוע (1900 - ערך ברירת מחדל)']);
const pFloors = rawBuildingData.floors || bInfo.floors;
if (pFloors) {
  dataRows.push(['מספר קומות', pFloors]);
  if (buildingData.floorsOverrideReason) dataRows.push(['', `⚠️ ${buildingData.floorsOverrideReason}`]);
}
const pHeight = buildingData.height || bInfo.height || buildingStats['גובה המבנה במטרים'];
if (pHeight) dataRows.push(['גובה מבנה', `${pHeight} מ׳`]);
const parcelClip = advancedLayers.buildingAreaOnParcel;
const pArea = (parcelClip?.isPartial ? parcelClip.buildingAreaOnParcel : null)
  || buildingData.area || bInfo.area || buildingStats['שטח במטרים רבועים'];
if (pArea) {
  dataRows.push(['שטח מבנה', `${pArea} מ"ר`]);
  if (parcelClip?.isPartial) {
    dataRows.push(['', `שטח מבנה כולל: ${parcelClip.totalBuildingArea} מ"ר (${parcelClip.coveragePercent}% על החלקה)`]);
  }
}
if (buildingData.buildingNumber) dataRows.push(['מספר מבנה', buildingData.buildingNumber]);
const landUse = fInput.landUse || bInfo.landUse || advancedLayers.landUse?.designation;
if (landUse) dataRows.push(['ייעוד קרקע', landUse]);
if (buildingData.buildingType) dataRows.push(['סוג מבנה', buildingData.buildingType]);
if (buildingData.hasColumns) dataRows.push(['עמודים', buildingData.hasColumns]);
if (buildingData.asbestLevel && buildingData.asbestLevel !== 'לא ידוע') dataRows.push(['סכנת אסבסט', buildingData.asbestLevel]);
if (buildingData.conservationSite?.isConservationSite) {
  dataRows.push(['מבנה לשימור', buildingData.conservationSite.designation || 'כן']);
} else if (buildingData.conservationArea?.inConservationZone) {
  dataRows.push(['שימור (אזור)', buildingData.conservationArea.areaName || buildingData.conservationArea.layerName || 'אזור שימור']);
}
const ownership = advancedLayers.ownership;
const mainOwnership = ownership?.find(p => String(p['גוש']) === String(propertyInfo.gush) && String(p['חלקה']) === String(propertyInfo.helka));
if (mainOwnership?.['סוג בעלות']) dataRows.push(['סוג בעלות', mainOwnership['סוג בעלות']]);
if (mainOwnership?.['שטח רשום (מ"ר)']) dataRows.push(['שטח רשום', `${mainOwnership['שטח רשום (מ"ר)']} מ"ר`]);
const socioCluster = socioEconomic['אשכול מדד 2021'] || socioEconomic['אשכול סוציו-אקונומי'];
if (socioCluster) dataRows.push(['אשכול סוציו-אקונומי', `${socioCluster}/10`]);
const socioFunc = socioEconomic['תפקוד עיקרי לאזור סטטיסטי'];
if (socioFunc) dataRows.push(['תפקוד עיקרי', socioFunc]);

const dataGridHTML = dataRows.length > 0 ? `
  <div class="data-grid">
    ${dataRows.map(([label, val]) => `<div class="data-row"><span class="data-label">${label}:</span><span class="data-val">${val}</span></div>`).join('')}
  </div>
` : '';

// --- Eligibility cards (shared with EligibilityCheck.js via computeEligibilityGoNoGo) ---
const eligGo = computeEligibilityGoNoGo(feasibilityData || {});
const eligTests = eligGo.tests;
const normalizedElig = eligGo.normalizedScore;

const ageEligTest = eligTests.find((t) => t.id === 'age');
const yearNum = ageEligTest?.evidence?.year_built ? Number(ageEligTest.evidence.year_built) : null;
const planTest = eligTests.find((t) => t.id === 'plan');
const section7778Plans = fInput.section7778Plans || [];
const has7778 = section7778Plans.length > 0;
const planEvidence = fInput.section23Plans?.[0];
const planLabel = planEvidence?.planNumber || planEvidence?.title || '';
const section7778Label = has7778 ? (section7778Plans[0].planNumber || section7778Plans[0].title || '') : '';

let planDescPdf;
if (has7778) {
  planDescPdf = `נמצאה תכנית${planLabel ? ' (' + planLabel + ')' : ''}, אך זוהתה תכנית 77/78${section7778Label ? ' (' + section7778Label + ')' : ''} המקפיאה היתרי בנייה`;
} else if (planTest.status === 'pass') {
  planDescPdf = `נמצאה תכנית רלוונטית${planLabel ? ' (' + planLabel + ')' : ''}`;
} else if (planTest.status === 'fail') {
  planDescPdf = 'לא נמצאה תכנית';
} else {
  planDescPdf = 'חסר מידע לגבי תכנית התחדשות עירונית';
}

const landUseForEligCard = fInput.landUse || bInfo.landUse || advancedLayers.landUse?.designation;

const buildEligCardDescPdf = (t) => {
  if (t.status === 'unknown') {
    return `<strong>${escapeHtmlPdf('חסר נתון קריטי')}</strong><br>${escapeHtmlPdf(t.missingImpact || '')}`;
  }
  if (t.id === 'age') {
    if (t.status === 'pass' && yearNum) return `שנת בנייה ${yearNum} - לפני 1980`;
    if (t.status === 'fail' && yearNum) return `שנת בנייה ${yearNum} - אחרי 1980`;
    return 'לא נמצאו נתוני גיל מהימנים';
  }
  if (t.id === 'plan') return planDescPdf;
  if (t.id === 'conservation') {
    if (t.status === 'pass') return 'לא נמצא שימור';
    if (t.status === 'fail') return 'מבנה לשימור';
    return 'חסר מידע לגבי שימור';
  }
  if (t.id === 'land_use') {
    if (landUseForEligCard) return `ייעוד: ${landUseForEligCard}`;
    return 'חסר מידע לגבי ייעוד קרקע';
  }
  return '';
};

const statusLabels = ELIGIBILITY_CARD_STATUS_LABELS;
const eligCards = eligTests.map((t) => ({
  title: t.title,
  status: t.status,
  desc: buildEligCardDescPdf(t),
}));
const eligScaleIndex = Math.min(4, Math.max(0, normalizedElig + 2));
const eligMarkerPct = (1 - ((eligScaleIndex + 0.5) / 5)) * 100;

const buildScaleSVG = (title, markerPct, labels) => {
  const W = 560, H = 72;
  const barY = 28, barH = 14, barR = 7;
  const padL = 10, padR = 10;
  const barW = W - padL - padR;
  const markerX = padL + (markerPct / 100) * barW;
  const segW = barW / labels.length;
  return `
    <div style="margin: 16px 0 20px;">
      <div style="font-weight:700;font-size:14px;color:${palette.primary};margin-bottom:8px;">${title}</div>
      <svg width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg" style="display:block;overflow:visible;">
        <defs>
          <linearGradient id="scaleGrad_${markerPct}" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stop-color="rgba(16,185,129,0.35)"/>
            <stop offset="25%" stop-color="rgba(34,197,94,0.35)"/>
            <stop offset="50%" stop-color="rgba(148,163,184,0.35)"/>
            <stop offset="75%" stop-color="rgba(245,158,11,0.35)"/>
            <stop offset="100%" stop-color="rgba(239,68,68,0.35)"/>
          </linearGradient>
        </defs>
        <rect x="${padL}" y="${barY}" width="${barW}" height="${barH}" rx="${barR}" fill="url(#scaleGrad_${markerPct})" stroke="#94a3b8" stroke-opacity="0.4" stroke-width="1"/>
        <circle cx="${markerX}" cy="${barY + barH / 2}" r="9" fill="#1a1a1a"/>
        <circle cx="${markerX}" cy="${barY + barH / 2}" r="12" fill="none" stroke="rgba(15,23,42,0.1)" stroke-width="3"/>
        ${labels.map((lbl, i) => {
          const tx = padL + segW * i + segW / 2;
          return `<text x="${tx}" y="${barY + barH + 18}" text-anchor="middle" font-size="11" fill="#555">${lbl}</text>`;
        }).join('')}
      </svg>
    </div>`;
};

const eligScaleLabels = ['גבוהה מאוד', 'גבוהה', 'בינונית', 'נמוכה', 'נמוכה מאוד'];
const eligScaleHTML = buildScaleSVG('סולם זכאות', eligMarkerPct, eligScaleLabels);

const eligHTML = `
  <p class="elig-section-intro">${escapeHtmlPdf(ELIGIBILITY_SECTION_INTRO)}</p>
  <div class="elig-grid">
    ${eligCards.map(c => `
      <div class="elig-card ${c.status}">
        <div class="elig-title">${c.title}</div>
        <div class="elig-status">${statusLabels[c.status]}</div>
        <div class="elig-desc">${c.desc}</div>
      </div>
    `).join('')}
  </div>
  ${eligScaleHTML}
`;

// --- Plans table ---
const plans = searchResults?.plans || [];
const urbanRenewalPlans = plans.filter(p => p.isPriority);
let plansTableHTML = '';
if (urbanRenewalPlans.length > 0) {
  plansTableHTML = `
    <h3>תכניות התחדשות עירונית (${urbanRenewalPlans.length})</h3>
    <table class="plans-table">
      <thead><tr><th>שם/מספר</th><th>סטטוס</th><th>סוג</th></tr></thead>
      <tbody>
        ${urbanRenewalPlans.slice(0, 15).map(p => `<tr><td>${p.planNumber || p.title || p.name || '—'}</td><td>${p.status || '—'}</td><td>${p.type || '—'}</td></tr>`).join('')}
      </tbody>
    </table>
  `;
}

// --- Transactions table (using correct field names: amount, size, beds, pricePerMeter) ---
const transactions = marketData.transactions || [];
let txnHTML = '';
if (transactions.length > 0) {
  // Show up to 20, sorted by date descending
  const sorted = [...transactions]
    .filter(t => t.date && t.pricePerMeter)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 20);
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('he-IL'); } catch { return d || '—'; } };
  txnHTML = `
    <h3>עסקאות אחרונות באזור (${transactions.length} סה"כ, מציג ${sorted.length})</h3>
    <table class="txn-table">
      <thead><tr><th>כתובת</th><th>מרחק</th><th>תאריך</th><th>מחיר עסקה</th><th>שטח</th><th>חדרים</th><th>מחיר למ"ר</th></tr></thead>
      <tbody>
        ${sorted.map(t => {
          const amt = t.amount || t.price || (t.pricePerMeter && t.size ? Math.round(t.pricePerMeter * t.size) : null);
          const rooms = t.beds || t.rooms;
          return `<tr>
            <td>${t.address || '—'}</td>
            <td>${t.distance !== undefined ? Math.round(t.distance) + ' מ\'' : '—'}</td>
            <td>${fmtDate(t.date)}</td>
            <td>${amt ? '₪' + Number(Math.round(amt)).toLocaleString() : '—'}</td>
            <td>${t.size ? t.size + ' מ"ר' : '—'}</td>
            <td>${rooms || '—'}</td>
            <td>${t.pricePerMeter ? '₪' + Math.round(t.pricePerMeter).toLocaleString() : '—'}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  `;
}

// --- Price trend chart (inline SVG for PDF) ---
let chartHTML = '';
if (transactions.length >= 2) {
  const filtered = [...transactions]
    .filter(t => t.pricePerMeter && t.date && t.pricePerMeter >= 1000 && t.pricePerMeter <= 200000)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (filtered.length >= 2) {
    const windowSize = Math.max(5, Math.floor(filtered.length / 10));
    const points = filtered.map((t, i) => {
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(filtered.length, i + Math.floor(windowSize / 2) + 1);
      const subset = filtered.slice(start, end);
      const avg = subset.reduce((s, x) => s + x.pricePerMeter, 0) / subset.length;
      return { date: new Date(t.date), price: Math.round(avg) };
    });

    const prices = points.map(p => p.price);
    const rawMin = Math.min(...prices);
    const rawMax = Math.max(...prices);
    // Add ~10% padding top and bottom to match Recharts auto-scaling
    const yPad = (rawMax - rawMin) * 0.1 || 500;
    const minP = Math.floor((rawMin - yPad) / 1000) * 1000;
    const maxP = Math.ceil((rawMax + yPad) / 1000) * 1000;
    const rangeP = maxP - minP || 1;
    const minT = points[0].date.getTime();
    const maxT = points[points.length - 1].date.getTime();
    const rangeT = maxT - minT || 1;

    const W = 660, H = 260, padL = 70, padR = 30, padT = 24, padB = 56;
    const cW = W - padL - padR, cH = H - padT - padB;

    const toX = (t) => padL + ((t.getTime() - minT) / rangeT) * cW;
    const toY = (p) => padT + cH - ((p - minP) / rangeP) * cH;

    const linePts = points.map(p => `${toX(p.date).toFixed(1)},${toY(p.price).toFixed(1)}`).join(' ');
    const areaPts = `${toX(points[0].date).toFixed(1)},${(padT + cH).toFixed(1)} ${linePts} ${toX(points[points.length - 1].date).toFixed(1)},${(padT + cH).toFixed(1)}`;

    // Y-axis: gridlines first, then labels on top
    let yGridLines = '';
    let yLabelTexts = '';
    for (let i = 0; i <= 4; i++) {
      const val = minP + (rangeP * i / 4);
      const y = toY(val);
      yGridLines += `<line x1="${padL}" x2="${W - padR}" y1="${y}" y2="${y}" stroke="#e8e8e8" stroke-width="0.5"/>`;
      const formatted = Math.round(val).toLocaleString();
      yLabelTexts += `<text x="${padL - 12}" y="${y + 4}" text-anchor="end" font-size="10" fill="#888" direction="ltr">${formatted}</text>`;
    }

    // X-axis: exactly 5 labels, short English format to avoid RTL overlap
    let xLabels = '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const labelCount = Math.min(5, points.length);
    for (let i = 0; i < labelCount; i++) {
      const idx = Math.round(i * (points.length - 1) / (labelCount - 1));
      const p = points[idx];
      const x = toX(p.date);
      const m = months[p.date.getMonth()];
      const y2d = String(p.date.getFullYear()).slice(-2);
      xLabels += `<text x="${x}" y="${padT + cH + 28}" text-anchor="middle" font-size="10" fill="#888">${m} '${y2d}</text>`;
    }

    chartHTML = `
      <h3>מגמת מחירים למ"ר</h3>
      <svg width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg" style="display:block;margin:12px auto;overflow:visible;" direction="ltr">
        ${yGridLines}
        <line x1="${padL}" x2="${padL}" y1="${padT}" y2="${padT + cH}" stroke="#ddd" stroke-width="0.5"/>
        <line x1="${padL}" x2="${W - padR}" y1="${padT + cH}" y2="${padT + cH}" stroke="#ddd" stroke-width="0.5"/>
        ${yLabelTexts}
        ${xLabels}
        <polygon points="${areaPts}" fill="rgba(69,123,157,0.10)" />
        <polyline points="${linePts}" fill="none" stroke="${palette.secondary}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
      </svg>
    `;
  }
}

// --- AI sections ---
const summaryHTML = summaryBlocks.map(b => `
  ${b.label ? `<div class="summary-label">${b.label}</div>` : ''}
  <div class="summary-text">${fmtPrint(b.text)}</div>
`).join('');

const showIlaTipPdf = isRegisteredAsIlaLease(searchResults);
const tipsPdfHTML = `
  <div class="ai-tips-wrap">
    <h3>${escapeHtmlPdf(AI_TIPS_SECTION_TITLE)}</h3>
    ${showIlaTipPdf ? `
    <div class="tips-tip-block">
      <div class="tips-tip-title">${escapeHtmlPdf(AI_TIP_ILA.title)}</div>
      <p>${escapeHtmlPdf(AI_TIP_ILA.body)}</p>
      <p class="tips-disclaimer">${escapeHtmlPdf(AI_TIP_ILA.disclaimer)}</p>
    </div>` : ''}
    <div class="tips-tip-block">
      <div class="tips-tip-title">${escapeHtmlPdf(AI_TIP_TABU.title)}</div>
      <p>${escapeHtmlPdf(AI_TIP_TABU.body)}</p>
      <p>${escapeHtmlPdf(AI_TIP_TABU.followUp)}</p>
    </div>
  </div>
`;

// Likelihood scale for AI section
const likelihoodScaleLabels = ['גבוה מאוד', 'גבוה', 'בינוני', 'נמוך', 'נמוך מאוד'];
const aiLikelihoodIndex = recommendation ? ({ very_high: 0, high: 1, medium: 2, low: 3, very_low: 4 }[recommendation.likelihood] ?? 2) : 2;
const aiMarkerPct = ((aiLikelihoodIndex + 0.5) / 5) * 100;
const likelihoodScaleHTML = recommendation ? buildScaleSVG('הסיכוי שפרויקט יקרה אצלכם', aiMarkerPct, likelihoodScaleLabels) : '';

const aiSectionHTML = recommendation ? `
  <hr class="section-divider">
  <h2>ניתוח הסוכן החכם של אורבניקס</h2>
  ${get('openingStatement') ? `<div class="opening">${get('openingStatement')}</div>` : ''}

  <h3>נתונים בסיסיים</h3>
  <div class="item"><div class="item-label">מיקום</div><p>${get('locationAnalysis')}</p></div>
  <div class="item"><div class="item-label">ייעוד קרקע</div><p>${get('landUseAnalysis')}</p></div>
  <div class="item"><div class="item-label">גיל מבנה</div><p>${get('yearBuiltAnalysis')}</p></div>

  <h3>מאפייני מבנה</h3>
  <div class="metric-inline">הערכת דירות: <strong>${estimatedApts}</strong> ${rangeLabel ? `— ${rangeLabel}` : ''}</div>
  <div class="item"><div class="item-label">ניתוח מבנה</div><p>${ba.analysis || ''}</p></div>
  <div class="item"><div class="item-label">קומת עמודים</div><p>${get('elevatedGroundFloorAnalysis')}</p></div>

  <h3>תכנון, מדיניות וסביבה</h3>
  <div class="item"><div class="item-label">שימור</div><p>${get('conservationAnalysis')}</p></div>
  <div class="item"><div class="item-label">סוציו-אקונומי</div><p>${get('socioEconomicAnalysis')}</p></div>
  <div class="item"><div class="item-label">תכניות</div><p>${get('plansAnalysis')}</p></div>
  <div class="item"><div class="item-label">תכנית 77/78</div><p>${get('section7778Analysis')}</p></div>

  <h3>סיכום והמלצות</h3>
  ${summaryHTML}
  ${tipsPdfHTML}

  <div class="likelihood-row">
    <span class="likelihood-label">הערכת סיכוי להתחדשות:</span>
    <span class="likelihood-val">${likelihoodLabels[recommendation.likelihood] || 'לא צוין'}</span>
  </div>
  ${likelihoodScaleHTML}
` : '';

// --- Full document ---
const pdfAddress = searchResults?.address || 'דוח';
const pdfDate = new Date().toLocaleDateString('he-IL');
const pdfTitle = `UrbaniX - ${pdfAddress} - ${pdfDate}`;


return `<!DOCTYPE html><html dir="rtl"><head>
      <meta charset="utf-8">
      <title>${pdfTitle}</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>${style}</style>
    </head><body>
      <h1>דוח התחדשות עירונית - אורבניקס</h1>
      <div class="subtitle">דוח מלא שהופק ב-${new Date().toLocaleDateString('he-IL')} בשעה ${new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</div>

      <h2>ריכוז נתוני הנכס</h2>
      ${dataGridHTML}

      <h2>בדיקת זכאות להתחדשות עירונית</h2>
      ${eligHTML}

      ${plansTableHTML ? `<h2>תכניות</h2>${plansTableHTML}` : ''}

      ${txnHTML || chartHTML ? `<h2 class="page-break">נתוני שוק</h2>${chartHTML}${txnHTML}` : ''}

      ${aiSectionHTML ? `<div class="page-break">${aiSectionHTML}</div>` : ''}

      <div class="footer">דוח זה הופק באמצעות מערכת אורבניקס &bull; www.urbanix.ai &bull; ${new Date().toLocaleDateString('he-IL')}</div>
    </body></html>`;
}
